import { FieldInput, HierarchicalOption } from '@/generated'
import { useSessionStorage } from '@/hooks'
import {
  HierarchicalFilterFragment,
  useHierarchicalFiltersByIdsQuery,
} from '@/ux/HierarchicalFilter/HierarchicalFilter.generated'
import { usePlatformOutletContext } from '@/ux/Platform/usePlatFormOutletContext'
import { SpinnerIosRegular } from '@fluentui/react-icons'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useFilterIds } from '../FilterIdProvider/FilterIdsProvider'
import { buildFilterInputsFromHierarchical } from './utils'

type HierarchicalFilterContextType = {
  hierarchicalFilters: HierarchicalFilterFragment[] | null | undefined
  hierarchicalFilterInputs: FieldInput[] | null | undefined
  hierarchicalFiltersLoaded: boolean
  updateHierarchicalFilters: (id: string, option: HierarchicalOption | undefined, depth: number) => void
  updateHierarchicalLeafSelections: (id: string, option: HierarchicalOption | undefined) => void
  multiSelectAll: (id: string, options: HierarchicalOption[] | null | undefined) => void
  multiClearAll: (id: string) => void
  clearHierFilterData: () => void
}

const HierarchicalFilterContext = createContext<HierarchicalFilterContextType>({
  hierarchicalFilters: undefined,
  hierarchicalFilterInputs: undefined,
  hierarchicalFiltersLoaded: false,
  updateHierarchicalFilters: () => {},
  updateHierarchicalLeafSelections: () => {},
  multiSelectAll: () => {},
  multiClearAll: () => {},
  clearHierFilterData: () => {},
})

export const HierarchicalFilterContextProvider = (props: { children: React.ReactNode }) => {
  const { hierarchicalFilterIds } = useFilterIds()
  const { appId, appKey, projectId } = usePlatformOutletContext()
  const [hierFiltersPersisted, setHierFiltersPersisted] = useSessionStorage<HierarchicalFilterFragment[]>(
    `${hierarchicalFilterIds}?pid=${projectId}`,
    []
  )
  const [hierarchicalFilters, setHierarchicalFilters] = useState<HierarchicalFilterFragment[]>(
    () => hierFiltersPersisted
  )
  const [hierarchicalFilterInputs, setHierarchicalFilterInputs] = useState<FieldInput[]>()

  const extraInputs = useMemo(
    () => [
      { name: 'appId', value: `${appId}` },
      { name: 'projectId', value: `${projectId}` },
    ],
    [appId, projectId]
  )

  const {
    data: _d,
    error: _e,
    loading: _l,
  } = useHierarchicalFiltersByIdsQuery({
    fetchPolicy: 'no-cache',
    skip: !!hierarchicalFilters.length || !hierarchicalFilterIds.length,
    onCompleted: (data) => {
      const nonNullFilters =
        data?.app?.hierarchicalFiltersByIds.filter((x): x is HierarchicalFilterFragment => x !== null) ?? []
      setHierarchicalFilters(nonNullFilters)
    },
    variables: {
      appId: appKey,
      projectId,
      filterIds: hierarchicalFilterIds,
      extraInputs,
    },
  })

  const hierarchicalFiltersLoaded = !!hierarchicalFilters.length || !hierarchicalFilterIds.length

  useEffect(() => {
    if (hierarchicalFilters.length && hierarchicalFilterIds.length) {
      setHierarchicalFilterInputs(buildFilterInputsFromHierarchical([...hierarchicalFilters]))
      setHierFiltersPersisted(hierarchicalFilters)
    }
  }, [hierarchicalFilters, setHierFiltersPersisted, hierarchicalFilterIds])

  const handlers = useMemo(
    () => ({
      updateHierarchicalFilters: (id: string, option: HierarchicalOption | undefined, depth: number) => {
        console.debug(`updateHierarchicalFilters ${id} ${JSON.stringify(option)} ${JSON.stringify(depth)}`)
        if (!option) {
          return
        }
        setHierarchicalFilters((prev) => {
          const updated = prev?.map((hier) => {
            if (hier.id !== id) {
              return hier
            }

            const _selection = hier.selection?.slice(0, depth) ?? []
            _selection.push(option)

            return {
              ...hier,
              selection: _selection,
              leafSelections: null,
            }
          })
          return updated
        })
      },

      updateHierarchicalLeafSelections: (id: string, option: HierarchicalOption | undefined) => {
        if (!option) {
          return
        }
        setHierarchicalFilters((prev) => {
          const updated = prev?.map((hier) => {
            if (hier.id !== id) {
              return hier
            }

            const currentSelections = hier.leafSelections ?? []
            const filtered = currentSelections.filter((it) => it.value !== option.value)
            const newSelections = filtered.length < currentSelections.length ? filtered : [...filtered, option]

            return {
              ...hier,
              leafSelections: newSelections,
            }
          })
          return updated
        })
      },

      multiSelectAll: (id: string, options: HierarchicalOption[] | null | undefined) => {
        setHierarchicalFilters((prev) => {
          const updated = prev?.map((hier) => {
            if (hier.id !== id) {
              return hier
            }

            return {
              ...hier,
              leafSelections: options,
            }
          })
          return updated
        })
      },

      multiClearAll: (id: string) => {
        setHierarchicalFilters((prev) => {
          const updated = prev?.map((hier) => {
            if (hier.id !== id) {
              return hier
            }

            return {
              ...hier,
              leafSelections: [],
            }
          })
          return updated
        })
      },

      clearHierFilterData: () => setHierarchicalFilters([]),
    }),
    []
  )

  if (!hierarchicalFiltersLoaded) {
    return (
      <div className="flex size-full items-center justify-center bg-muted">
        <div className="flex items-center gap-x-2">
          <SpinnerIosRegular fontSize={20} className="animate-spin" />
          <span>Loading page</span>
        </div>
      </div>
    )
  }

  return (
    <HierarchicalFilterContext.Provider
      value={{
        hierarchicalFilters,
        hierarchicalFilterInputs,
        hierarchicalFiltersLoaded,
        ...handlers,
      }}
    >
      {props.children}
    </HierarchicalFilterContext.Provider>
  )
}

export const useHierarchicalFilterContext = () => {
  const context = useContext(HierarchicalFilterContext)
  if (!context) throw new Error('useHierarchicalFilterContext must be used within a HierarchicalFilterContextProvider')
  return context
}
